/* eslint-disable react/prop-types */
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import NavBar from 'features/common/NavBar';
import Footer from 'features/common/Footer';
import classnames from 'classnames';
import T from 'i18n';
import {
  selectPermissions,
  selectDocumentsOnlyWhenInRoom,
  selectRoomToken,
  selectCurrentCase,
  selectEvidenceViewEnabled,
  selectBundleTabPage,
} from 'common/selectors';
import PageNotFound from 'features/common/PageNotFound';
import RefreshButton from './RefreshButton';
import FullScreenButton from './FullScreenButton';
import Splitter, { SplitDirection } from '@devbookhq/splitter';
import { useFetchTheatreData } from 'features/viewing/redux/fetchTheatreData';
import { useLeaveAGroup } from 'features/viewing/redux/leaveAGroup';
import { usePrevious } from 'utils/hooks';
import { Authenticated } from 'features/auth/withAuth';
import FileViewer from './FileViewer';

const TheatreDocOnly = (props) => {
  const { hearingRoomMode } = props;
  const [fullScreen, setFullScreen] = useState(!!document.fullscreenElement);
  // Initialize with a safe default; update based on messages length in an effect
  const [splitterSizes, setSplitterSizes] = useState([100, 0]);

  const { fetchTheatreData, theatreData, privateTheatreData } = useFetchTheatreData();
  const { leaveAGroup } = useLeaveAGroup();
  const fetchCaseDetailsPending = useSelector((state) => state.case.fetchCaseDetailsPending);
  const fetchCasesPending = useSelector((state) => state.home.fetchCasesPending);
  const documentsOnlyWhenInRoom = useSelector(selectDocumentsOnlyWhenInRoom);
  const isJoinedToRoom = useSelector((state) => selectRoomToken(state, hearingRoomMode));
  const { hearingRoom } = useSelector(selectPermissions);
  const aCase = useSelector(selectCurrentCase);
  const previousMessagesRef = useRef([]);
  const td = hearingRoomMode === 'public' ? theatreData : privateTheatreData;
  const allMessages = td?.messages || [];

  const sortedMessages = [...allMessages].filter(Boolean).sort((a, b) => {
    const aTimestamp = a?.timestamp ?? a?.message?.timestamp ?? Number.NEGATIVE_INFINITY;
    const bTimestamp = b?.timestamp ?? b?.message?.timestamp ?? Number.NEGATIVE_INFINITY;
    return bTimestamp - aTimestamp;
  });

  const remainingMessages = [...sortedMessages];
  const pickByConnection = (connectionId) => {
    if (!connectionId) return null;
    const idx = remainingMessages.findIndex(
      (msg) =>
        (msg?.connectionId || msg?.message?.connectionId) &&
        (msg?.connectionId || msg?.message?.connectionId) === connectionId,
    );
    if (idx === -1) return null;
    return remainingMessages.splice(idx, 1)[0];
  };

  const latestMessages = [];

  previousMessagesRef.current.forEach((prevMessage) => {
    if (latestMessages.length === 2) return;
    const connectionId = prevMessage?.connectionId || prevMessage?.message?.connectionId;
    const matched = pickByConnection(connectionId);
    if (matched) {
      latestMessages.push(matched);
    }
  });

  for (const message of remainingMessages) {
    if (latestMessages.length === 2) break;
    const connectionId = message?.connectionId || message?.message?.connectionId;
    const alreadyIncluded = latestMessages.some(
      (msg) => (msg?.connectionId || msg?.message?.connectionId) === connectionId,
    );
    if (alreadyIncluded) continue;
    latestMessages.push(message);
  }

  previousMessagesRef.current = latestMessages;

  const messages = latestMessages;
  const hasMessages = messages.length > 0;
  const btpActive = useSelector(selectBundleTabPage);
  const isEvidenceViewEnabled = useSelector(selectEvidenceViewEnabled);

  const prevDocumentsOnlyWhenInRoom = usePrevious(documentsOnlyWhenInRoom, null);
  const prevIsJoinedToRoom = usePrevious(isJoinedToRoom, null);

  function fullscreenchanged() {
    setFullScreen(!!document.fullscreenElement);
  }

  function resize() {
    setFullScreen(window.innerHeight === window.screen.height);
  }

  const onMount = () => {
    if (!documentsOnlyWhenInRoom || isJoinedToRoom) fetchTheatreData({ hearingRoomMode });
    document.addEventListener('fullscreenchange', fullscreenchanged);
    window.addEventListener('resize', resize);

    return () => {
      leaveAGroup('receive');
      document.removeEventListener('fullscreenchange', fullscreenchanged);
      window.removeEventListener('resize', resize);
    };
  };
  useEffect(onMount, []);

  const isPublic = hearingRoomMode === 'public';
  const isPrivate = hearingRoomMode !== 'public';

  const showDocuments = hearingRoom.canViewDocuments(hearingRoomMode);
  const modeText = T.translate(isPublic ? 'case.publicHearingRoom' : 'case.privateHearingRoom');

  const brandText = [
    [aCase.name + `${aCase.claimNumber ? ` ${aCase.claimNumber}` : ''}`, modeText]
      .filter(Boolean)
      .join(' - '),
    T.translate('case.documentPresentationOnly'),
  ]
    .filter(Boolean)
    .join(' ');

  let docBeingPresentedText = brandText;

  if (hasMessages) {
    const msg = messages[0];
    const { currentFileDetails } = msg;

    if (msg.fileType === 'mp4') {
      docBeingPresentedText += ` - ${msg.fileId}`;
    } else {
      const name = currentFileDetails?.name;
      if (btpActive) {
        const prefix = currentFileDetails?.globalPagePrefix;
        const tab = currentFileDetails?.tab;
        const parts = [];
        if (prefix !== undefined && prefix !== null) parts.push(prefix);
        if (tab !== undefined && tab !== null) parts.push(tab);
        const pageInfo = parts.length ? `[${parts.join('/')}] ` : '';
        const namePart = name ? ` ${name}` : '';
        docBeingPresentedText = `${pageInfo}${msg.fileId}${namePart}`;
      } else {
        // When BTP is inactive, omit global page details entirely
        const namePart = name ? ` ${name}` : '';
        docBeingPresentedText = `${msg.fileId}${namePart}`;
      }
    }
  } else {
    docBeingPresentedText = '';
  }

  function handleResizeFinished(gutterIdx, newSizes) {
    setSplitterSizes(newSizes);
  }

  useEffect(() => {
    if (fetchCaseDetailsPending) return;

    if (documentsOnlyWhenInRoom) {
      if (prevIsJoinedToRoom !== isJoinedToRoom) {
        if (isJoinedToRoom) {
          fetchTheatreData({ hearingRoomMode });
        } else {
          leaveAGroup('receive');
        }
      }
    } else {
      if (prevDocumentsOnlyWhenInRoom !== documentsOnlyWhenInRoom) {
        fetchTheatreData({ hearingRoomMode });
      }
    }
  }, [
    fetchCaseDetailsPending,
    prevDocumentsOnlyWhenInRoom,
    documentsOnlyWhenInRoom,
    prevIsJoinedToRoom,
    isJoinedToRoom,
    hearingRoomMode,
    fetchTheatreData,
    leaveAGroup,
  ]);

  useEffect(() => {
    setSplitterSizes(messages.length > 1 ? [50, 50] : [100, 0]);
  }, [messages.length]);

  // Keep hooks order consistent by placing early returns after all hooks
  if (fetchCasesPending || fetchCaseDetailsPending) return null;
  if (!hearingRoom.isShow || !showDocuments) return <PageNotFound />;
  return (
    <div
      className={classnames({ dark: isPublic, blue: isPrivate }, 'case-theatre', {
        isEvidenceViewEnabled,
      })}
    >
      <NavBar
        showLogo
        theme={classnames({ dark: isPublic, blue: isPrivate })}
        brandText={docBeingPresentedText.length > 0 ? docBeingPresentedText : brandText}
        additionalButton={[
          <RefreshButton key="refresh-button" />,
          <FullScreenButton key="fullscreen-button" />,
        ].filter(Boolean)}
        messages={messages}
      />

      <div
        className={classnames('content', { 'full-screen': fullScreen })}
        style={{
          background: isEvidenceViewEnabled ? 'black' : 'white',
          color: isEvidenceViewEnabled ? 'white' : 'inherit',
        }}
      >
        {documentsOnlyWhenInRoom && !isJoinedToRoom ? (
          <h4
            style={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              lineHeight: '2rem',
            }}
          >
            {T.translate('case.needToJoinRoom')}
          </h4>
        ) : hasMessages ? (
          <Splitter
            direction={SplitDirection.Horizontal}
            initialSizes={splitterSizes}
            minWidths={[170, 170]}
            minHeights={[170, 170]}
            onResizeFinished={handleResizeFinished}
          >
            {messages.map((fileData, index) => {
              return (
                <FileViewer
                  key={index}
                  fileData={fileData}
                  hearingRoomMode={hearingRoomMode}
                  showAnnotationsInTheatreMode
                />
              );
            })}
          </Splitter>
        ) : (
          <h4
            style={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {T.translate('case.waitingForPresenter')}
          </h4>
        )}
      </div>
      {!fullScreen && <Footer theme={classnames({ dark: isPublic, blue: isPrivate })} />}
    </div>
  );
};

TheatreDocOnly.propTypes = {
  hearingRoomMode: PropTypes.string,
};

export default (props) => (
  <Authenticated forceLogin>
    <TheatreDocOnly {...props} />
  </Authenticated>
);
